/**
 * The price's line items and extras, edited on the page where they print.
 *
 * The same table the customer reads — item, qty, price inc GST, total inc GST
 * — with each cell an input that looks like the text until it is pointed at.
 * Rows come from the draft, not the view, because the view drops a line with
 * no description and a row just added has none yet. Options' own lines stay
 * in the panel: a package card is a button, and a button cannot hold inputs.
 * The panel still does the rest — catalog, services, tax, "offered with".
 */
import { unitPriceIncTax } from '@fieldops/shared'
import { useEffect, useRef, useState } from 'react'
import { cn } from '../../lib/cn.js'
import { money } from '../../lib/format.js'
import { Icon } from '../Icon.js'
import { AutoTextarea } from './CanvasBlock.js'
import { blankLine, centsOf, dollarsOf, incTaxPrice, lineFigures, type DraftLine } from './draft.js'
import { ProposalPricing, type Choice, type ProposalView } from './ProposalPage.js'
import { useLineDefaults } from './PricingInspector.js'

export interface LineEditing {
  lines: DraftLine[]
  mode: 'quote' | 'template'
  /** `group` coalesces typing into one undo step. */
  onPatch: (key: string, next: Partial<DraftLine>, group?: string) => void
  onRemove: (key: string) => void
  onAdd: (line: DraftLine) => void
}

/** `ProposalPricing` with the line items and extras as editable tables. */
export function EditablePricing({
  view,
  editing,
  choice,
  onChoice,
}: {
  view: ProposalView
  editing: LineEditing
  choice: Choice
  onChoice: (choice: Choice) => void
}) {
  const included = editing.lines.filter((line) => !line.isOptional && line.optionKey === null)
  const extras = editing.lines.filter((line) => line.isOptional)
  // With packages, line items are the legacy case: shown only if there are some.
  const showIncluded = view.options.length === 0 || included.length > 0

  return (
    <ProposalPricing
      title={null}
      body={null}
      proposal={view}
      choice={choice}
      onChoice={onChoice}
      bare
      edit={{
        included: showIncluded ? (
          <CanvasLineTable lines={included} editing={editing} place={{ isOptional: false, optionKey: null }} />
        ) : null,
        extras: <CanvasLineTable lines={extras} editing={editing} place={{ isOptional: true, optionKey: null }} />,
      }}
    />
  )
}

const cell =
  'w-full rounded-md bg-transparent px-1.5 py-0.5 tabular-nums transition-colors placeholder:text-ink-300 hover:bg-ink-50 focus:bg-white focus:ring-1 focus:ring-[var(--p-brand)] focus:outline-none'

function CanvasLineTable({
  lines,
  editing,
  place,
}: {
  lines: DraftLine[]
  editing: LineEditing
  place: Pick<DraftLine, 'isOptional' | 'optionKey'>
}) {
  const defaults = useLineDefaults()
  const [fresh, setFresh] = useState<string | null>(null)
  const extras = place.isOptional
  const noun = extras ? 'extra' : 'line item'
  const columns = extras
    ? 'grid-cols-[1.25rem_minmax(0,1fr)_3rem_5.5rem_5.5rem_1.5rem] sm:grid-cols-[1.5rem_minmax(0,1fr)_3.5rem_6.5rem_6.5rem_1.75rem]'
    : 'grid-cols-[minmax(0,1fr)_3rem_5.5rem_5.5rem_1.5rem] sm:grid-cols-[minmax(0,1fr)_3.5rem_6.5rem_6.5rem_1.75rem]'

  const add = () => {
    const line = blankLine(defaults, { ...place, ...(editing.mode === 'template' ? { unitPrice: null } : {}) })
    editing.onAdd(line)
    setFresh(line.key)
  }

  return (
    <div className="mt-3 overflow-hidden rounded-xl bg-white ring-1 ring-ink-200">
      <div
        aria-hidden
        className={cn(
          'grid gap-x-2 bg-[var(--p-brand-soft)] px-3 py-2 text-[11px] font-bold tracking-[0.08em] text-[var(--p-accent-text)] uppercase sm:px-4',
          columns,
        )}
      >
        {extras && <span />}
        <span className="px-1.5">Item</span>
        <span className="text-right">Qty</span>
        <span className="text-right">Price inc GST</span>
        <span className="text-right">Total inc GST</span>
        <span />
      </div>
      <ul aria-label={extras ? 'Optional extras' : 'Line items'} className="divide-y divide-ink-100">
        {lines.map((line, index) => {
          const name = `${extras ? 'Extra' : 'Line'} ${index + 1}`
          return (
            <li
              key={line.key}
              className={cn('group/line grid items-start gap-x-2 px-3 py-2.5 text-sm sm:px-4', columns)}
            >
              {extras && <span aria-hidden className="mt-1 size-5 rounded-md bg-white ring-2 ring-ink-300 ring-inset" />}
              <span className="min-w-0">
                <AutoTextarea
                  label={`${name} description`}
                  value={line.description}
                  placeholder="Describe the work"
                  autoFocus={line.key === fresh}
                  className={cn(cell, 'whitespace-pre-line text-ink-800')}
                  onChange={(value) => editing.onPatch(line.key, { description: value }, `line-${line.key}-description`)}
                />
                {line.source ? <span className="block truncate px-1.5 text-[11px] text-ink-500">{line.source}</span> : null}
              </span>
              <input
                aria-label={`${name} quantity`}
                inputMode="decimal"
                value={line.quantity}
                className={cn(cell, 'text-right text-ink-600')}
                onChange={(event) => editing.onPatch(line.key, { quantity: event.target.value }, `line-${line.key}-quantity`)}
              />
              <IncTaxPriceInput
                label={`${name} price inc GST`}
                line={line}
                placeholder={editing.mode === 'template' ? 'Client' : '0.00'}
                onChange={(incTax) =>
                  editing.onPatch(
                    line.key,
                    incTaxPrice(editing.mode === 'template' ? incTax : (incTax ?? 0), line.taxRateBasisPoints),
                    `line-${line.key}-price`,
                  )
                }
              />
              <span className="py-0.5 text-right font-semibold text-ink-900 tabular-nums">
                {line.unitPrice === null ? (
                  <span className="text-xs font-normal text-ink-500">Per client</span>
                ) : (
                  money(lineFigures(line).lineTotalIncTax)
                )}
              </span>
              <button
                type="button"
                aria-label={`Remove ${name.toLowerCase()}`}
                title={`Remove this ${noun}`}
                onClick={() => editing.onRemove(line.key)}
                className="grid size-6 place-items-center rounded text-ink-400 opacity-0 transition-opacity group-hover/line:opacity-100 group-focus-within/line:opacity-100 hover:bg-ink-100 hover:text-ink-800 focus-visible:opacity-100"
              >
                <Icon name="close" className="size-3.5" />
              </button>
            </li>
          )
        })}
      </ul>
      <button
        type="button"
        onClick={add}
        className={cn(
          'flex w-full items-center gap-1.5 px-4 py-2.5 text-left font-sans text-[13px] font-medium text-ink-500 hover:bg-[var(--p-brand-soft)]/60 hover:text-ink-900',
          lines.length > 0 && 'border-t border-ink-100',
        )}
      >
        <Icon name="plus" className="size-3.5" />
        Add {extras ? 'an extra' : 'a line item'}
      </button>
    </div>
  )
}

/**
 * The unit price, typed inc GST because that is the column it sits in, and
 * kept that way (`unitPriceIncTax`) so $280 stays $280. Holds the typing
 * while focused, like `PriceInput`, so "12." is not rewritten as "12" under
 * the cursor.
 */
function IncTaxPriceInput({
  label,
  line,
  placeholder,
  onChange,
}: {
  label: string
  line: DraftLine
  placeholder: string
  onChange: (incTax: number | null) => void
}) {
  const incTax = line.unitPrice === null ? null : unitPriceIncTax({ unitPrice: line.unitPrice, unitPriceIncTax: line.unitPriceIncTax, taxRateBasisPoints: line.taxRateBasisPoints })
  const [text, setText] = useState(dollarsOf(incTax))
  const focused = useRef(false)

  useEffect(() => {
    if (!focused.current) setText(dollarsOf(incTax))
  }, [incTax])

  return (
    <input
      aria-label={label}
      inputMode="decimal"
      value={text}
      placeholder={placeholder}
      className={cn(cell, 'text-right text-ink-600')}
      onFocus={() => {
        focused.current = true
      }}
      onBlur={() => {
        focused.current = false
        setText(dollarsOf(incTax))
      }}
      onChange={(event) => {
        setText(event.target.value)
        onChange(centsOf(event.target.value))
      }}
    />
  )
}
